import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { PresenceActivity } from '@atlaskit/editor-common/collab';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';

import { ExperienceFailure } from '@confluence/experience-tracker/entry-points/component.ts';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import type { PresenceContentMode, PresencePageContentType } from '../presenceTypes.ts';
import { useTransformedParticipants } from '../presence-store/index.ts';
import type { NestedAgentPresenceChild } from '../presence-avatar-group/index.ts';
import type { PresenceAvatarGroupProps } from '../presence-avatar-group/PresenceAvatarGroup.tsx';
import { PresenceAvatarGroup } from '../presence-avatar-group/index.ts';

import { usePagePresence } from './usePagePresence.ts';

const i18n = defineMessages({
	anonymousUser: {
		id: 'team-presence.avatar-group.anonymous-user',
		defaultMessage: 'Anonymous Person',
		description:
			'The default user name provided for anonymous users when displayed in the avatar group within the real-time presence experience',
	},
});

export type PresenceProps = Pick<
	PresenceAvatarGroupProps,
	'size' | 'maxCount' | 'isOverflowSeparated'
> & {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresencePageContentType;
	activity: PresenceActivity;
	useBootstrapToken?: boolean;
	spaceKey?: string;
	analyticSource?: string;
	children?: NestedAgentPresenceChild;
};

export const PagePresence: FC<PresenceProps> = ({
	contentId,
	contentMode,
	contentType,
	activity,
	useBootstrapToken,
	spaceKey,
	analyticSource,
	children,
	size,
	maxCount,
	isOverflowSeparated,
}) => {
	const { formatMessage } = useIntl();

	const { sessionId, networkError, totalParticipants, presenceProvider } = usePagePresence(
		contentId,
		contentType,
		contentMode,
		activity,
		{ useBootstrapToken, spaceKey },
	);

	const participants = useTransformedParticipants({
		activity,
		sessionId,
		anonymousName: formatMessage(i18n.anonymousUser),
	});

	if (networkError) {
		return (
			<ExperienceFailure name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} error={networkError} />
		);
	}

	const nonGuestShowsDefaultProfile: boolean = participants.some(
		(participant) => participant.src === '' && !participant.isGuest,
	);
	const showsDefaultProfile: boolean = participants.some((participant) => participant.src === '');

	return (
		<>
			<UFOCustomData
				data={{
					nonGuestShowsDefaultProfile,
					showsDefaultProfile,
				}}
			/>
			<PresenceAvatarGroup
				contentId={contentId}
				contentMode={contentMode}
				contentType={contentType}
				analyticSource={analyticSource}
				size={size}
				maxCount={maxCount}
				isOverflowSeparated={isOverflowSeparated}
				participants={participants}
				totalParticipants={totalParticipants}
				presenceProvider={presenceProvider}
			>
				{children}
			</PresenceAvatarGroup>
		</>
	);
};
